import { CodecError } from "./codec.js";

/**
 * Files, encrypted the same way everything else is (SPEC §3).
 *
 * The content blob covers what a person *typed* — a file's name, the caption
 * under a photo. It never covered the bytes, and for a while that made "end to
 * end encrypted" a half-truth: the messages were ciphertext and the photographs
 * in them sat on the server's disk exactly as taken.
 *
 * ## The envelope
 *
 *   magic "VMST" | version | iv (12) | ciphertext
 *
 * and inside the ciphertext, before the file itself:
 *
 *   mime length (2, big-endian) | mime (utf-8) | the bytes
 *
 * Two things follow from that shape, and both are the point.
 *
 * **The type is inside.** `image/jpeg` on a row tells whoever holds the disk
 * that this is a photograph, and `application/pdf` that it is a document — so
 * the type is content, and it travels under the same key. The server stores
 * `application/octet-stream` and means it.
 *
 * **The magic is outside.** A file uploaded before the space was locked is
 * still on that disk in the clear, under the same URL, and has to keep opening.
 * Reading four bytes says which kind of file this is, so one route serves both
 * and a catch-up pass can convert them one at a time without anything going
 * dark in between.
 */

const MAGIC = new Uint8Array([0x56, 0x4d, 0x53, 0x54]); // "VMST"
const VERSION = 1;
const IV_BYTES = 12;
const HEADER = MAGIC.length + 1 + IV_BYTES;

const subtle = (): SubtleCrypto => {
  if (typeof crypto === "undefined" || !crypto.subtle) {
    throw new CodecError("WebCrypto is unavailable — a secure context is required");
  }
  return crypto.subtle;
};

/**
 * Is this an envelope, or a file from before the space was locked?
 *
 * Four bytes and a version. It cannot be fooled into decrypting a plaintext
 * JPEG, and it cannot mistake an envelope for one: no image format in the
 * allowlist begins "VMST".
 */
export function isSealed(bytes: Uint8Array): boolean {
  if (bytes.length < HEADER) return false;
  for (let i = 0; i < MAGIC.length; i++) if (bytes[i] !== MAGIC[i]) return false;
  return bytes[MAGIC.length] === VERSION;
}

/** What the server should be told a sealed file is, which is nothing. */
export const SEALED_MIME = "application/octet-stream";

export async function sealMedia(
  key: CryptoKey,
  mime: string,
  bytes: Uint8Array,
): Promise<Uint8Array> {
  const label = new TextEncoder().encode(mime);
  if (label.length > 0xffff) throw new CodecError("that is not a media type");

  const plain = new Uint8Array(2 + label.length + bytes.length);
  plain[0] = (label.length >> 8) & 0xff;
  plain[1] = label.length & 0xff;
  plain.set(label, 2);
  plain.set(bytes, 2 + label.length);

  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const sealed = new Uint8Array(
    await subtle().encrypt({ name: "AES-GCM", iv: iv as BufferSource }, key, plain),
  );

  const out = new Uint8Array(HEADER + sealed.length);
  out.set(MAGIC, 0);
  out[MAGIC.length] = VERSION;
  out.set(iv, MAGIC.length + 1);
  out.set(sealed, HEADER);
  return out;
}

export interface OpenedMedia {
  mime: string;
  bytes: Uint8Array;
}

export async function openMedia(key: CryptoKey, sealed: Uint8Array): Promise<OpenedMedia> {
  if (!isSealed(sealed)) throw new CodecError("this file is not sealed");

  let plain: Uint8Array;
  try {
    plain = new Uint8Array(
      await subtle().decrypt(
        { name: "AES-GCM", iv: sealed.slice(MAGIC.length + 1, HEADER) as BufferSource },
        key,
        sealed.slice(HEADER) as BufferSource,
      ),
    );
  } catch {
    // GCM authentication failed: the wrong key, or bytes that were altered on
    // the way. Indistinguishable from here, and neither is worth guessing at.
    throw new CodecError("this file will not open with this key");
  }

  if (plain.length < 2) throw new CodecError("this file will not open with this key");
  const length = (plain[0]! << 8) | plain[1]!;
  if (plain.length < 2 + length) throw new CodecError("this file will not open with this key");

  return {
    mime: new TextDecoder().decode(plain.subarray(2, 2 + length)),
    // A copy rather than a view: the caller hands this to Blob and the backing
    // buffer still holds the whole decrypted file behind it.
    bytes: plain.slice(2 + length),
  };
}
